import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import OptiBuildLogo, { OptiBuildLogoSimple } from './OptibuildLogo';
import { useAuth } from '../src/hooks/useAuth';
import { usePermission } from '../src/utils/permissions';
import {
  Menu,
  LayoutDashboard,
  FolderOpen,
  Calendar,
  FileText,
  HelpCircle,
  Send,
  ClipboardCheck,
  ShieldAlert,
  CheckSquare,
  Calculator,
  TrendingUp,
  Handshake,
  Users,
  Package,
  ShoppingCart,
  Truck,
  Archive,
  ClipboardList,
  DollarSign,
  Receipt,
  CreditCard,
  Banknote,
  Clock,
  Code,
  BarChart3,
  Settings,
  LogOut,
  Building2,
  Layers,
  User
} from 'lucide-react';

interface CollapsibleSidebarProps {
  activeModule: string;
  onNavigate: (path: string) => void;
  onLogout: () => void;
}

export function CollapsibleSidebar({ activeModule, onNavigate, onLogout }: CollapsibleSidebarProps) {
  const { user } = useAuth();
  const { canAccessRoute } = usePermission();
  const [isCollapsed, setIsCollapsed] = useState(() => window.innerWidth < 768);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['Dashboard', 'Project Management', 'Pre-Construction & Planning', 'Procurement & Inventory', 'Financials & Contracts', 'Reports & Analytics', 'Settings'])
  );

  // Auto-collapse on mobile; don't auto-expand on desktop (user controls that)
  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth < 768) setIsCollapsed(true);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const menuSections = [
    {
      title: 'Dashboard',
      items: [
        { icon: LayoutDashboard, label: 'Overview', path: '/dashboard' }
      ]
    },
    {
      title: 'Pre-Construction & Planning',
      items: [
        { icon: Handshake, label: 'Bids & Tenders', path: '/bids' },
        { icon: Calculator, label: 'BOQ Management', path: '/boq' },
        { icon: TrendingUp, label: 'Estimates', path: '/estimates' }
      ]
    },
    {
      title: 'Project Management',
      items: [
        { icon: FolderOpen, label: 'All Projects', path: '/projects' },
        { icon: Calendar, label: 'Schedules & Tasks', path: '/schedules' },
        { icon: FileText, label: 'Daily Logs', path: '/daily-logs' },
        { icon: FileText, label: 'Documents', path: '/documents' },
        { icon: HelpCircle, label: 'RFIs', path: '/rfis' },
        { icon: Send, label: 'Submittals', path: '/submittals' },
        { icon: ClipboardCheck, label: 'Inspections & QA/QC', path: '/inspections' },
        { icon: ShieldAlert, label: 'Safety Incidents', path: '/safety-incidents' },
        { icon: CheckSquare, label: 'Punch Lists', path: '/punch-lists' }
      ]
    },
    {
      title: 'Procurement & Inventory',
      items: [
        { icon: Users, label: 'Suppliers', path: '/suppliers' },
        { icon: ShoppingCart, label: 'Purchase Orders', path: '/purchase-orders' },
        { icon: Truck, label: 'Goods Received', path: '/goods-received' },
        { icon: Archive, label: 'Inventory', path: '/inventory' },
        { icon: ClipboardList, label: 'Material Requisitions', path: '/material-requisitions' }
      ]
    },
    {
      title: 'Financials & Contracts',
      items: [
        { icon: DollarSign, label: 'Budget & Cost Control', path: '/budget-cost' },
        { icon: FileText, label: 'Contracts', path: '/contracts' },
        { icon: Receipt, label: 'Change Orders', path: '/change-orders' },
        { icon: Receipt, label: 'Invoices (AP)', path: '/invoices-ap' },
        { icon: CreditCard, label: 'Payment Applications (AR)', path: '/payment-applications-ar' },
        { icon: Banknote, label: 'Payments', path: '/payments' },
        { icon: Clock, label: 'Timesheets', path: '/timesheets' },
        { icon: Code, label: 'Cost Codes', path: '/cost-codes' }
      ]
    },
    {
      title: 'Reports & Analytics',
      items: [
        { icon: BarChart3, label: 'Reports & Analytics', path: '/reports' }
      ]
    },
    {
      title: 'Settings',
      items: [
        { icon: Building2, label: 'Organization Profile', path: '/settings/organization' },
        { icon: User, label: 'User Management', path: '/settings/users' }
      ]
    }
  ];

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  const toggleSection = (sectionTitle: string) => {
    if (isCollapsed) return; // Don't allow section toggle when collapsed
    
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionTitle)) {
        newSet.delete(sectionTitle);
      } else {
        newSet.add(sectionTitle);
      }
      return newSet;
    });
  };

  return (
    <div className={`flex flex-col h-full bg-sidebar border-r border-sidebar-border transition-all duration-300 ${isCollapsed ? 'w-16' : 'w-72'}`}>
      {/* Header */}
      <div className={`flex items-center border-b border-sidebar-border p-4 ${isCollapsed ? 'flex-col gap-2' : 'justify-between'}`}>
        {isCollapsed ? (
          <>
            <OptiBuildLogoSimple size="md" className="text-sidebar-primary" />
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleSidebar}
              className="p-1.5 hover:bg-sidebar-accent"
              title="Expand sidebar"
            >
              <Menu className="h-4 w-4 text-sidebar-foreground" />
            </Button>
          </>
        ) : (
          <>
            <OptiBuildLogo size="md" variant="full" />
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleSidebar}
              className="p-1.5 hover:bg-sidebar-accent"
              title="Collapse sidebar"
            >
              <Menu className="h-4 w-4 text-sidebar-foreground" />
            </Button>
          </>
        )}
      </div>

      {/* Navigation */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 space-y-2 sidebar-scrollbar">
        {menuSections.map((section) => {
          const visibleItems = section.items.filter(item => canAccessRoute(item.path));
          if (visibleItems.length === 0) return null;

          const isExpanded = expandedSections.has(section.title);
          const hasActiveItem = visibleItems.some(item => item.path === activeModule);
          
          return (
            <div key={section.title}>
              {!isCollapsed && (
                <button
                  onClick={() => toggleSection(section.title)}
                  className={`w-full text-left h-8 px-2 mb-1 text-xs font-semibold uppercase tracking-wider rounded hover:bg-sidebar-accent/50 transition-colors ${
                    hasActiveItem ? 'text-sidebar-primary' : 'text-sidebar-foreground/70'
                  }`}
                >
                  {section.title}
                </button>
              )}
              
              {(isCollapsed || isExpanded) && (
                <div className={`space-y-1 ${!isCollapsed ? 'ml-2' : ''}`}>
                  {visibleItems.map((item) => {
                    const isActive = activeModule === item.path;
                    return (
                      <Button
                        key={item.path}
                        variant="ghost"
                        size="sm"
                        onClick={() => onNavigate(item.path)}
                        className={`w-full justify-start h-8 px-2 ${
                          isActive
                            ? 'bg-sidebar-primary text-sidebar-primary-foreground hover:bg-sidebar-primary/90'
                            : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
                        } ${isCollapsed ? 'px-2' : 'px-3'}`}
                        title={isCollapsed ? item.label : undefined}
                      >
                        <item.icon className="h-4 w-4 flex-shrink-0" />
                        {!isCollapsed && (
                          <span className="ml-3 text-sm whitespace-normal break-words leading-tight">{item.label}</span>
                        )}
                      </Button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-sidebar-border">
        <div className={`space-y-2 ${isCollapsed ? 'flex flex-col items-center' : ''}`}>
          {!isCollapsed && (
            <button
              onClick={() => onNavigate('/settings/profile')}
              className="w-full flex items-center gap-2 p-2 bg-sidebar-accent rounded-lg hover:bg-sidebar-primary/10 transition-colors text-left"
              title="View my profile"
            >
              <div className="w-8 h-8 bg-sidebar-primary rounded-full flex items-center justify-center shrink-0">
                <User className="h-4 w-4 text-sidebar-primary-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-sidebar-foreground truncate">
                  {user?.name || 'User'}
                </p>
                <p className="text-xs text-sidebar-foreground/70 truncate">
                  {user?.jobTitle || 'Role'}
                </p>
              </div>
            </button>
          )}
          {isCollapsed && (
            <button
              onClick={() => onNavigate('/settings/profile')}
              className="w-8 h-8 bg-sidebar-primary rounded-full flex items-center justify-center hover:bg-sidebar-primary/80 transition-colors mx-auto"
              title={user?.name || 'My profile'}
            >
              <User className="h-4 w-4 text-sidebar-primary-foreground" />
            </button>
          )}
          
          <Button
            variant="ghost"
            size="sm"
            onClick={onLogout}
            className={`w-full text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground ${
              isCollapsed ? 'px-2' : 'justify-start px-3'
            }`}
            title={isCollapsed ? "Logout" : undefined}
          >
            <LogOut className="h-4 w-4" />
            {!isCollapsed && <span className="ml-3 text-sm">Logout</span>}
          </Button>
        </div>
      </div>

    </div>
  );
}